/**
 * SendEmailOption
 * 
 * @Author: Focci
 * @Date: 2024-07-11 14:27:14
 * @Last Modified by: Focci
 * @Last Modified time: 2024-07-11 14:27:14
 */

import Image from 'next/image'
import useLogin from '@hook/useLogin'
import useTracking from '@hook/useTracking'
import { imageSize } from '@lib/image/loader'
import { forEach, isArray } from 'lodash-es'
import { Checkbox } from '@comp/material'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { fetchPost } from '@lib/fetch/client'
import { useEnquiryInfo } from '@hook/useEnquiryInfo'
import { getPhonePrefix } from '@lib/utils'
import { formatDate } from '@lib/datetime'
import { getListingEnquiryProps, getEnquiryProps } from '../agent-card/lib'
import PhoneButton from '../agent-card/PhoneButton'

function SendEmailOptionAgent({ agent }) {
  return (
    <div className="flex items-center gap-2">
      <Image 
        src={agent.avatar || ''}
        unoptimized={true}
        width={90}
        height={90}
        alt={agent.name}
        className="w-18 h-18 object-cover object-top rounded-full shrink-0 bg-cf0"
      />
      <div className="flex-auto">
        <div className="font-medium text-f.8">{agent.name}</div>
        <div className="text-meta text-f.7">{agent.language}</div>
        <div className="mt-2">
          <PhoneButton 
            agentId={agent.id} 
            phone={agent.phone} 
            trackParams={{ agentId: agent.id, agencyOfficeId: agent.officeId }} 
          />
        </div>
      </div>
    </div>
  )
}

/**
 * 获取邮件内容
 */
function getEmailBody(house, type, data) {
  const code = house?.code ? ` ref:${house.code}` : ''
  const datetimeStr = data?.time_start
    ? (formatDate(data.time_start, ' DD MMM YYYY dddd hha', 'en') || ' ') // 13 July 2024 Saturday 12am
    : ''
  
  const tmp = [`I am interested in this property${code},`]

  // 收藏
  if(type === '10') {
    tmp.push('and I have added this property into my favourite list,')
    tmp.push('I would like to request property documents,')
  }

  // 拍卖
  if(type === '3') {
    tmp.push(`and I have added auction${datetimeStr} to my calendar,`)
    tmp.push('I would like to arrange a private viewing,')
  }

  // 开放日
  if(type === '2') {
    tmp.push(`and I have added open home${datetimeStr} to my calendar,`)
    tmp.push('I would like to arrange a private viewing,')
  }

  tmp.push('please send me more information')
  
  return tmp.join(' ')
}

function SendEmailOption({
  house,
  data, // 开放日、拍卖数据
  type, // 2 开放日, 3 拍卖, 10 收藏
}, ref) {
  const te = useTranslations('enquiry')
  const { user } = useLogin()
  const { userName, userEmail, userMobile } = useEnquiryInfo()
  const { pushTrackListing, pushTrack } = useTracking()

  const [checked, setChecked] = useState(true)

  const agents = useMemo(() => {
    const tmp = []
    const { agents: agentsData, simpleAgents, __islist__: isList, office } = house || {}
    const target = isList ? simpleAgents : agentsData
    forEach(
      (isArray(target) ? target : []).slice(0, 2), 
      (it) => tmp.push({
        avatar: imageSize(isList ? it.avatar : it.icon, 90),
        name: it.name,
        language: it.language,
        phone: isList ? it.phone : it.phone_number,
        id: it.id,
        officeId: office?.id
      })
    )
    
    return tmp
  }, [house])
  
  /**
   * 发送邮件
   */
  const sendEnquireEmail = useCallback(() => {
    if(!checked || house?.isPublisher) return
    
    // eslint-disable-next-line dot-notation
    const { houseId, formProps } = house?.['__islist__'] 
      ? getListingEnquiryProps(house) 
      : getEnquiryProps(house)
    
    const body = getEmailBody(house, type, data)
    const agentId = agents[0]?.id

    const param = { 
      email: userEmail || user?.email || '',
      id: houseId, 
      agentId,
      name: userName || user?.nickname,
      phoneNumber: userMobile || user?.mobile_number,
      type: formProps.type,
      body, 
      spec: type === '10' ? 'limReport' : 'basic',
      countryCode: getPhonePrefix(),
      favorite: 1, // 添加这个参数，发送enquiry不会被限制
    }

    try{
      (param?.email || param?.phoneNumber) && fetchPost('user/enquiry', param) 
    } catch(err) { /**/ }

    // fire event
    pushTrackListing('click_email_sent', house)
    pushTrack('click_email_sent', { houseId, agentId, agencyOfficeId: house?.office?.id })
  }, [
    agents, checked, pushTrackListing, pushTrack, house, type, data,
    user, userEmail, userMobile, userName
  ])

  useImperativeHandle(ref, () => ({
    sendEnquireEmail
  }))

  return (
    <div className="flex flex-col gap-4">
      <label className="flex items-center gap-1 cursor-pointer">
        <Checkbox
          checked={checked}
          onChange={() => setChecked(!checked)}
        />
        <span>{te('sendEmailGetMoreInfo')}</span>
      </label>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        {agents.map((agent) => (
          <SendEmailOptionAgent key={agent.id} agent={agent} />
        ))}
      </div>
    </div>
  )
}

export default forwardRef(SendEmailOption)
